import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Edit,
  Trash2,
  Plus,
  X,
  Upload,
  Image as ImageIcon,
} from "lucide-react";
import DeleteAlertModal from "../../components/AdminSideComponents/DashBoardFooter/Common/DeleteAlertModal";
import EditProjectModal from "../../components/AdminSideComponents/DashBoardFooter/AdminProject/EditProjectModal";

const ProjectSkeleton = () => (
  <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200 animate-pulse">
    <div className="w-full h-52 bg-gray-300"></div>
    <div className="p-5 space-y-4">
      <div className="h-6 bg-gray-300 rounded w-3/4"></div>
      <div className="h-4 bg-gray-300 rounded w-full"></div>
      <div className="h-4 bg-gray-300 rounded w-2/3"></div>
      <div className="space-y-2">
        <div className="h-4 bg-gray-300 rounded w-1/2"></div>
        <div className="h-3 bg-gray-300 rounded w-3/4"></div>
        <div className="h-3 bg-gray-300 rounded w-1/2"></div>
      </div>
      <div className="flex gap-2">
        <div className="w-1/3 h-24 bg-gray-300 rounded-lg"></div>
        <div className="w-1/3 h-24 bg-gray-300 rounded-lg"></div>
        <div className="w-1/3 h-24 bg-gray-300 rounded-lg"></div>
      </div>
    </div>
  </div>
);

const AddProjectModal = ({ isOpen, onClose, onProjectAdded }) => {
  const [formData, setFormData] = useState({
    projectName: "",
    description: "",
    projectType: "comingsoon",
    images: [],
    amenities: [""],
    specifications: [{ heading: "", subHeadings: [""] }],
    extraFacilities: [""],
    location: "",
    popularPlaces: [{ name: "", distance: "" }],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});

  // Cloudinary configuration - Replace with your actual cloud name and upload preset
  const CLOUDINARY_CLOUD_NAME = `${process.env.REACT_APP_CLOUDINARY_NAME}`;
  const CLOUDINARY_UPLOAD_PRESET = `${process.env.REACT_APP_UPLOAD_PRESET}`;

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].map((item, i) => (i === index ? value : item)),
    }));
  };

  const addArrayItem = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: [
        ...prev[field],
        field === "popularPlaces"
          ? { name: "", distance: "" }
          : field === "specifications"
          ? { heading: "", subHeadings: [""] }
          : "",
      ],
    }));
  };

  const removeArrayItem = (field, index) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const handleSpecificationChange = (
    specIndex,
    field,
    value,
    subIndex = null
  ) => {
    setFormData((prev) => ({
      ...prev,
      specifications: prev.specifications.map((spec, i) => {
        if (i === specIndex) {
          if (subIndex !== null) {
            return {
              ...spec,
              subHeadings: spec.subHeadings.map((sub, j) =>
                j === subIndex ? value : sub
              ),
            };
          }
          return { ...spec, [field]: value };
        }
        return spec;
      }),
    }));
  };

  // Cloudinary upload function
  const uploadToCloudinary = async (file, index) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", CLOUDINARY_UPLOAD_PRESET);
    formData.append("cloud_name", CLOUDINARY_CLOUD_NAME);

    try {
      setUploadProgress((prev) => ({ ...prev, [index]: 0 }));

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: "POST",
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      setUploadProgress((prev) => ({ ...prev, [index]: 100 }));
      return data.secure_url;
    } catch (error) {
      console.error("Cloudinary upload error:", error);
      alert("Failed to upload image. Please try again.");
      setUploadProgress((prev) => ({ ...prev, [index]: undefined }));
      return null;
    }
  };

  const removeSubHeading = (specIndex, subIndex) => {
    setFormData((prev) => ({
      ...prev,
      specifications: prev.specifications.map((spec, i) => {
        if (i === specIndex) {
          return {
            ...spec,
            subHeadings: spec.subHeadings.filter((_, j) => j !== subIndex),
          };
        }
        return spec;
      }),
    }));
  };

  const addSubHeading = (specIndex) => {
    setFormData((prev) => ({
      ...prev,
      specifications: prev.specifications.map((spec, i) => {
        if (i === specIndex) {
          return {
            ...spec,
            subHeadings: [...spec.subHeadings, ""],
          };
        }
        return spec;
      }),
    }));
  };

  // Handle file selection and upload
  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    setIsUploading(true);
    const uploadPromises = files.map((file, index) =>
      uploadToCloudinary(file, index)
    );

    try {
      const uploadedUrls = await Promise.all(uploadPromises);
      const validUrls = uploadedUrls.filter((url) => url !== null);

      setFormData((prev) => ({
        ...prev,
        images: [...prev.images, ...validUrls],
      }));
    } catch (error) {
      console.error("Error uploading images:", error);
    } finally {
      setIsUploading(false);
      setUploadProgress({});
    }
  };

  // Remove image from the list
  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.images.length === 0) {
      alert("Please upload at least one image");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/projects/add-project`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            amenities: formData.amenities.filter((amenity) => amenity.trim()),
            extraFacilities: formData.extraFacilities.filter((facility) =>
              facility.trim()
            ),
            specifications: formData.specifications.filter((spec) =>
              spec.heading.trim()
            ),
            popularPlaces: formData.popularPlaces.filter((place) =>
              place.name.trim()
            ),
          }),
        }
      );

      if (response.ok) {
        onProjectAdded();
        onClose();
        setFormData({
          projectName: "",
          description: "",
          projectType: "residentials", // Reset to default
          images: [],
          amenities: [""],
          specifications: [{ heading: "", subHeadings: [""] }],
          extraFacilities: [""],
          location: "",
          popularPlaces: [{ name: "", distance: "" }],
        });
      } else {
        alert("Failed to add project");
      }
    } catch (error) {
      console.error("Error adding project:", error);
      alert("Error adding project");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const handlePopularPlaceChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      popularPlaces: prev.popularPlaces.map((place, i) =>
        i === index ? { ...place, [field]: value } : place
      ),
    }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-[#001330]">Add New Project</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Name
              </label>
              <input
                type="text"
                required
                value={formData.projectName}
                onChange={(e) =>
                  handleInputChange("projectName", e.target.value)
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Location
              </label>
              <input
                type="text"
                required
                value={formData.location}
                onChange={(e) => handleInputChange("location", e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Project Type
              </label>
              <select
                required
                value={formData.projectType}
                onChange={(e) =>
                  handleInputChange("projectType", e.target.value)
                }
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent bg-white"
              >
                <option value="coming soon">Coming Soon</option>
                <option value="ongoing">On Going</option>
                <option value="completed">Complted</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              required
              value={formData.description}
              onChange={(e) => handleInputChange("description", e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent"
              rows={3}
            />
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Project Images
            </label>

            {/* Upload Button */}
            <div className="mb-4">
              <label className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-[#ED6049] hover:bg-orange-50 transition-colors">
                <div className="flex flex-col items-center">
                  {isUploading ? (
                    <>
                      <Upload
                        className="animate-bounce mb-2 text-[#ED6049]"
                        size={32}
                      />
                      <span className="text-sm text-gray-600">
                        Uploading...
                      </span>
                    </>
                  ) : (
                    <>
                      <ImageIcon className="mb-2 text-gray-400" size={32} />
                      <span className="text-sm text-gray-600">
                        Click to upload images
                      </span>
                    </>
                  )}
                </div>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={isUploading}
                />
              </label>
            </div>

            {/* Upload Progress */}
            {Object.keys(uploadProgress).length > 0 && (
              <div className="mb-4">
                {Object.entries(uploadProgress).map(([index, progress]) => (
                  <div key={index} className="mb-2">
                    <div className="flex justify-between text-sm text-gray-600 mb-1">
                      <span>Image {parseInt(index) + 1}</span>
                      <span>{progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-[#ED6049] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Uploaded Images Preview */}
            {formData.images.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {formData.images.map((image, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={image}
                      alt={`Upload ${index + 1}`}
                      className="w-full h-24 object-cover rounded-lg border"
                    />
                    <button
                      type="button"
                      onClick={() => removeImage(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amenities
              </label>
              {formData.amenities.map((amenity, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={amenity}
                    onChange={(e) =>
                      handleArrayChange("amenities", index, e.target.value)
                    }
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem("amenities", index)}
                    className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem("amenities")}
                className="text-[#ED6049] hover:bg-orange-50 px-3 py-1 rounded-lg"
              >
                + Add Amenity
              </button>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Extra Facilities
              </label>
              {formData.extraFacilities.map((facility, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={facility}
                    onChange={(e) =>
                      handleArrayChange(
                        "extraFacilities",
                        index,
                        e.target.value
                      )
                    }
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem("extraFacilities", index)}
                    className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Remove
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => addArrayItem("extraFacilities")}
                className="text-[#ED6049] hover:bg-orange-50 px-3 py-1 rounded-lg"
              >
                + Add Facility
              </button>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-[#001330] mb-4">
              Specifications
            </h3>
            {formData.specifications.map((spec, specIndex) => (
              <div key={specIndex} className="mb-6 border-b pb-6">
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    placeholder="Heading (e.g., Structure, Flooring)"
                    value={spec.heading}
                    onChange={(e) =>
                      handleSpecificationChange(
                        specIndex,
                        "heading",
                        e.target.value
                      )
                    }
                    className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => removeArrayItem("specifications", specIndex)}
                    className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Remove
                  </button>
                </div>

                <div className="ml-4 space-y-2">
                  {spec.subHeadings.map((subHeading, subIndex) => (
                    <div key={subIndex} className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Sub-heading"
                        value={subHeading}
                        onChange={(e) =>
                          handleSpecificationChange(
                            specIndex,
                            "subHeadings",
                            e.target.value,
                            subIndex
                          )
                        }
                        className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent"
                      />
                      <button
                        type="button"
                        onClick={() => removeSubHeading(specIndex, subIndex)}
                        className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-lg"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => addSubHeading(specIndex)}
                    className="text-[#ED6049] hover:bg-orange-50 px-3 py-1 rounded-lg ml-2"
                  >
                    + Add Sub-heading
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addArrayItem("specifications")}
              className="text-[#ED6049] hover:bg-orange-50 px-3 py-1 rounded-lg"
            >
              + Add Specification
            </button>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-medium text-[#001330] mb-4">
              Nearby Popular Places
            </h3>
            {formData.popularPlaces.map((place, index) => (
              <div
                key={index}
                className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Place Name
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., AIIMS, Lingaraj Temple"
                    value={place.name}
                    onChange={(e) =>
                      handlePopularPlaceChange(index, "name", e.target.value)
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Distance
                  </label>
                  <input
                    type="text"
                    placeholder="e.g., 2km, 5km"
                    value={place.distance}
                    onChange={(e) =>
                      handlePopularPlaceChange(
                        index,
                        "distance",
                        e.target.value
                      )
                    }
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#ED6049] focus:border-transparent"
                  />
                </div>
                <div className="flex items-end">
                  <button
                    type="button"
                    onClick={() => removeArrayItem("popularPlaces", index)}
                    className="w-full px-3 py-3 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={() => addArrayItem("popularPlaces")}
              className="text-[#ED6049] hover:bg-orange-50 px-3 py-1 rounded-lg"
            >
              + Add Popular Place
            </button>
          </div>

          <div className="border-t pt-6">
            <button
              type="submit"
              disabled={isSubmitting || isUploading}
              className="w-full bg-[#001330] text-white py-3 px-4 rounded-lg hover:bg-[#002244] transition duration-300 disabled:opacity-50"
            >
              {isSubmitting ? "Adding Project..." : "Add Project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Project = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredProject, setHoveredProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editProjectId, setEditProjectId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/projects/all-projects`
      );
      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  const handleProjectAdded = () => {
    fetchProjects();
  };

  const handleEdit = (projectId) => {
    setEditProjectId(projectId);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (project) => {
    setProjectToDelete(project);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!projectToDelete) return;

    setDeleteLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/projects/delete-project/${projectToDelete._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.ok) {
        setIsDeleteModalOpen(false);
        setProjectToDelete(null);
        fetchProjects();
      } else {
        const errorData = await response.json();
        alert(
          `Failed to delete project: ${errorData.message || "Unknown error"}`
        );
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      alert("Error deleting project. Please try again.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setProjectToDelete(null);
  };

  return (
    <div className="mt-6 mb-12 px-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold text-[#001330]">Manage Projects</h1>
        <button
          onClick={() => setIsModalOpen(true)}
          className="bg-[#001330] text-white px-4 py-2 rounded-lg hover:bg-[#002244] transition duration-300 flex items-center gap-2"
        >
          <Plus size={20} />
          Add Project
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {loading ? (
          Array.from({ length: 6 }).map((_, index) => (
            <ProjectSkeleton key={index} />
          ))
        ) : projects.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="text-gray-500 text-lg">No projects found</p>
          </div>
        ) : (
          projects.map((project) => (
            <div
              key={project._id}
              className="bg-white rounded-2xl shadow-lg overflow-hidden border border-[#ED6049] relative group"
              onMouseEnter={() => setHoveredProject(project._id)}
              onMouseLeave={() => setHoveredProject(null)}
            >
              {/* Hover overlay with edit/delete buttons */}
              {hoveredProject === project._id && (
                <div className="absolute top-3 right-3 flex gap-2 z-10">
                  <button
                    onClick={() => handleEdit(project._id)}
                    className="bg-blue-500 text-white p-2 rounded-full hover:bg-blue-600 transition duration-200 shadow-lg"
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(project)}
                    className="bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition duration-200 shadow-lg"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}

              <div className="w-full h-52">
                <img
                  src={
                    project.images && project.images[0]
                      ? project.images[0]
                      : "/placeholder-image.jpg"
                  }
                  alt={project.projectName}
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-5 space-y-4">
                <h2 className="text-xl font-semibold text-[#ED6049]">
                  {project.projectName}
                </h2>
                <p className="text-sm text-gray-600">{project.description}</p>
                <p className="text-sm font-medium text-[#001330]">
                  📍 {project.location}
                </p>

                <div>
                  <h4 className="font-semibold text-[#ED6049] mb-1">
                    Amenities:
                  </h4>
                  <ul className="list-disc list-inside text-sm text-gray-700">
                    {project.amenities &&
                      project.amenities.map((amenity, i) => (
                        <li key={i}>{amenity}</li>
                      ))}
                  </ul>
                </div>

                <div className="flex gap-2 mt-4">
                  {project.images &&
                    project.images
                      .slice(1, 4)
                      .map((img, i) => (
                        <img
                          key={i}
                          src={img}
                          className="w-1/3 h-24 object-cover rounded-lg"
                          alt={`Preview ${i}`}
                        />
                      ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      <AddProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onProjectAdded={handleProjectAdded}
      />

      <DeleteAlertModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        dltType="project"
        dltName={projectToDelete?.projectName}
        onConfirm={handleDeleteConfirm}
        dltLoading={deleteLoading}
      />

      <EditProjectModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        projectId={editProjectId}
        onProjectUpdated={fetchProjects}
      />
    </div>
  );
};

export default Project;
